export type StockUnitStatus = "IN_STOCK" | "RESERVED" | "DISPATCHED" | "ISSUED" | "SCRAPPED" | "CANCELLED";

export const STOCK_STATUS_LABELS: Record<string, string> = {
  IN_STOCK: "In stock",
  RESERVED: "Reserved",
  DISPATCHED: "Dispatched",
  ISSUED: "Issued",
  SCRAPPED: "Scrapped",
  CANCELLED: "Cancelled",
};

export const MOVEMENT_LABELS: Record<string, string> = {
  OPENING: "Opening stock",
  GRN_IN: "Goods received",
  GRN_CANCEL: "Receipt cancelled",
  PRODUCTION_IN: "From production",
  DISPATCH_OUT: "Dispatched",
  ISSUE_TO_PRODUCTION: "Issued to production",
  RETURN_FROM_PRODUCTION: "Returned from production",
  JOBWORK_OUT: "Sent for job work",
  JOBWORK_IN: "Received from job work",
  TRANSFER_OUT: "Transfer out",
  TRANSFER_IN: "Transfer in",
  ADJUST_PLUS: "Adjustment (+)",
  ADJUST_MINUS: "Adjustment (-)",
  SALES_RETURN: "Sales return",
  DISPATCH_CANCEL: "Dispatch cancelled",
  RESERVE: "Reserved for order",
  UNRESERVE: "Reservation released",
};

export const ADJUSTMENT_REASONS: Record<string, string> = {
  DAMAGE: "Damaged",
  SHORTAGE: "Shortage found",
  EXCESS_FOUND: "Excess found",
  RECOUNT: "Physical recount",
  QUALITY_REJECT: "Rejected on quality",
  SAMPLE_USED: "Used for samples",
  OTHER: "Other",
};

export const SUPPLIER_TYPES: Record<string, string> = {
  YARN: "Yarn / fibre",
  FABRIC: "Fabric",
  CHEMICAL: "Dyes and chemicals",
  PROCESSOR: "Job worker / processor",
  OTHER: "Other",
};

export const GRADES: Record<string, string> = { FRESH: "Fresh", SECOND: "Second", REJECT: "Reject" };

export interface WarehouseRecord {
  id: string;
  name: string;
  code: string;
  address: string | null;
  city: string | null;
  state: string | null;
  is_default: boolean;
  notes: string | null;
  status: boolean;
  unit_count?: number;
}

export interface SupplierRecord {
  id: string;
  code: string;
  name: string;
  supplier_type: string;
  contact_person: string | null;
  phone: string | null;
  email: string | null;
  gstin: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  payment_terms: string | null;
  notes: string | null;
  status: boolean;
}

export interface StockSummaryRow {
  product_id: string;
  variant_id: string | null;
  unit: string;
  product_name: string;
  product_code: string;
  product_type: string | null;
  variant_name: string | null;
  variant_code: string | null;
  color_hex: string | null;
  unit_count: number;
  lot_count: number;
  total_qty: number;
  reserved_qty: number;
  available_qty: number;
}

export interface StockUnitRecord {
  id: string;
  unit_no: string;
  barcode?: string | null;
  product_id: string;
  variant_id: string | null;
  lot_id: string;
  warehouse_id: string;
  location: string | null;
  quantity: number | string;
  initial_quantity: number | string;
  unit: string;
  net_weight: number | string | null;
  gross_weight: number | string | null;
  grade: string;
  unit_status: StockUnitStatus;
  goods_receipt_id: string | null;
  notes: string | null;
  reserved_order_id?: string | null;
  reserved_order_number?: string | null;
  /** From StockForLine: held for the line being looked at */
  reserved_here?: boolean;
  product?: { id: string; name: string; code: string; product_type?: string | null };
  variant?: { id: string; name: string; code: string; color_hex?: string | null } | null;
  lot?: { id: string; lot_number: string };
  warehouse?: { id: string; name: string };
  movements?: LedgerRow[];
}

export interface LedgerRow {
  id: string;
  created_on: string;
  movement_type: string;
  unit_id: string;
  unit_no: string | null;
  product_name: string | null;
  product_code: string | null;
  variant_name: string | null;
  lot_number: string | null;
  warehouse_name: string | null;
  quantity: number;
  unit: string;
  balance_after: number;
  ref_type: string | null;
  ref_number: string | null;
  reason: string | null;
  notes: string | null;
  created_by_name: string | null;
}

export interface LotRow {
  id: string;
  lot_number: string;
  product_name: string;
  product_code: string;
  variant_name: string | null;
  variant_code: string | null;
  supplier_name: string | null;
  source_type: string;
  source_ref: string | null;
  lot_date: string;
  unit: string | null;
  unit_count: number;
  on_hand_qty: number;
  received_qty: number;
}

export interface GoodsReceiptLine {
  id: string;
  product_id: string;
  variant_id: string | null;
  lot_id: string;
  unit: string;
  unit_count: number;
  quantity: number | string;
  product?: { id: string; name: string; code: string };
  variant?: { id: string; name: string; code: string } | null;
  lot?: { id: string; lot_number: string };
}

export interface GoodsReceiptRecord {
  id: string;
  grn_number: string;
  receipt_type: "PURCHASE" | "OPENING";
  receipt_date: string;
  receipt_status: "RECEIVED" | "CANCELLED";
  supplier_invoice_no: string | null;
  supplier_challan_no: string | null;
  transporter: string | null;
  vehicle_number: string | null;
  notes: string | null;
  cancelled_reason: string | null;
  supplier?: { id: string; name: string } | null;
  warehouse?: { id: string; name: string };
  lines: GoodsReceiptLine[];
  unit_count?: number;
  units?: StockUnitRecord[];
}

/** A roll being typed into a receipt or adjustment form */
export interface RollDraft {
  unit_no: string;
  quantity: string;
  net_weight: string;
  gross_weight: string;
  grade: string;
  location: string;
}

export interface ReceiptLineDraft {
  key: string;
  product_id: string;
  product_label: string;
  variant_id: string;
  lot_number: string;
  unit: string;
  variants: Array<{ id: string; name: string; code: string }>;
  rolls: RollDraft[];
}

export const emptyRoll = (): RollDraft => ({ unit_no: "", quantity: "", net_weight: "", gross_weight: "", grade: "FRESH", location: "" });

export const fmtQty = (n: number | string | null | undefined, unit?: string | null) => {
  if (n == null || n === "") return "-";
  const v = Number(n);
  return `${v.toLocaleString("en-IN", { maximumFractionDigits: 3 })}${unit ? ` ${unit}` : ""}`;
};

export interface StockForLineResponse {
  line: { id: string; order_id: string; product_name: string; variant_name: string | null; unit: string; quantity: number; dispatched_qty: number };
  units: StockUnitRecord[];
  lots: Array<{ lot_id: string; lot_number: string; unit_count: number; quantity: number }>;
  /** Stock of the same product held for other orders */
  reserved_elsewhere_qty: number;
}

export interface QcDefect {
  defect: string;
  points: number;
  count: number;
}

export interface QcRecord {
  id: string;
  inspection_number: string;
  unit_id: string;
  work_order_id: string | null;
  product_id: string;
  unit_no: string;
  product_name: string;
  lot_number: string;
  inspected_qty: number | string;
  unit: string;
  defects: QcDefect[];
  total_points: number;
  points_per_100: number | string;
  result: "PASS" | "SECOND" | "REJECT";
  grade_before: string;
  grade_after: string;
  notes: string | null;
  created_on: string;
  by?: string | null;
}

export const QC_RESULT_LABELS: Record<string, string> = { PASS: "Pass", SECOND: "Second", REJECT: "Reject" };

export interface QcSummary {
  totals: { inspected: number; pass: number; second: number; reject: number; avg_points: number | null };
  by_product: Array<{ product_id: string; product_name: string; inspected: number; pass: number; second: number; reject: number; avg_points: number | null }>;
  defects: Array<{ defect: string; occurrences: number; points: number }>;
}

export interface AgeingData {
  buckets: Array<{ key: string; label: string }>;
  rows: Array<Record<string, any> & { product_id: string; product_name: string; variant_name: string | null; unit: string; rolls: number; total_qty: number; oldest_days: number }>;
  totals: Record<string, Record<string, number>>;
}

export interface MoversData {
  days: number;
  rows: Array<{ product_id: string; product_name: string; product_code: string; unit: string; on_hand: number; sold: number; per_day: number; days_cover: number | null; last_sold_on: string | null; kind: "FAST" | "SLOW" | "DEAD" }>;
}
